import * as React from "react";
import {
  DataGrid,
  GridColDef,
  gridPageCountSelector,
  GridPagination,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { Grid2, Stack } from "@mui/material";
import MuiPagination from "@mui/material/Pagination";
import { TablePaginationProps } from "@mui/material/TablePagination";

interface IGridTableProps {
  rows: Array<any>;
  columns: GridColDef[];
  pageSizeOptions?: number[];
  initialPageSize?: number;
  initialPage?: number;
  title?: React.ReactNode;
  showSearchBar?: boolean;
  onButtonClick?: () => void;
  isPaginationHide?: boolean;
}

export const GridTable: React.FC<IGridTableProps> = ({
  rows,
  columns,
  pageSizeOptions = [5, 10],
  initialPageSize = 10,
  initialPage = 0,
  onButtonClick,
  isPaginationHide = false,
}) => {
  const [paginationModel, setPaginationModel] = React.useState({
    page: initialPage,
    pageSize: initialPageSize,
  });

  function Pagination({
    page,
    onPageChange,
    className,
  }: Pick<TablePaginationProps, "page" | "onPageChange" | "className">) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    return pageCount > 1 ? (
      <MuiPagination
        color="primary"
        className={className}
        count={pageCount}
        page={page + 1}
        onChange={(event, newPage) => {
          onPageChange(event as any, newPage - 1);
        }}
      />
    ) : null;
  }

  function CustomPagination(props: any) {
    return <GridPagination ActionsComponent={Pagination} {...props} />;
  }

  const dataGridHeight = 52 * 10; // Fixed height for 10 rows

  function NoRowsOverlay() {
    return (
      <Stack
        height="100%"
        alignItems="center"
        justifyContent="center"
        fontSize="1rem"
        color={"var(--color-border-model)"}
      >
        No data available.
      </Stack>
    );
  }

  function NoResultsOverlay() {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        Filter returns no result.
      </Stack>
    );
  }

  //setting by default column definition throughout application
  columns = columns.map((column, index) => {
    column.align = "left";
    column.headerAlign = "left";
    column.description = column.headerName;
    column.filterable = false;
    column.sortable = column.sortable || false;
    column.flex = column.width ? undefined : 1;
    column.headerClassName = "super-app-theme--header";
    return column;
  });

  return (
    <Grid2 sx={{ width: "100%", padding: "0" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DataGrid
          disableRowSelectionOnClick
          rows={rows}
          columns={columns}
          disableColumnMenu
          hideFooterPagination={isPaginationHide}
          pagination
          slots={{
            pagination: CustomPagination,
            noRowsOverlay: NoRowsOverlay,
            noResultsOverlay: NoResultsOverlay,
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={(newPaginationModel) =>
            setPaginationModel(newPaginationModel)
          }
          pageSizeOptions={pageSizeOptions}
          sx={{
            bgcolor: "white",
            height: dataGridHeight,
            border: 0,
            "& .super-app-theme--header": {
              backgroundColor: "#e0e0e0",
              fontSize: "1rem",
              fontWeight: 700,
            },
            "& .super-app-theme--header .caption": {
              fontSize: "1rem",
              fontWeight: 700,
            },
            p: "10px 20px",
            overflowY: "auto",
            "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus":
              {
                border: "1px solid #f5f5f5 !important",
                outline: "1px solid #f5f5f5 !important",
              },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "1rem",
              fontWeight: 700,
            },
            "& .MuiDataGrid-sortIcon": {
              color: "#000000",
            },
            "& .MuiDataGrid-cell": {
              color: "#1e1e1e",
              fontSize: "1rem",
            },
            "& .MuiLink-root": {
              color: "info.main",
              textDecoration: "none",
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        />
      </div>
    </Grid2>
  );
};
